import type { Viewport } from '@deck.gl/core';
import { TileLayer, _Tileset2D as Tileset2D } from '@deck.gl/geo-layers';
import { BitmapLayer } from '@deck.gl/layers';
import type { BitmapBoundingBox, BitmapLayerProps } from '@deck.gl/layers';
import { parse } from '@loaders.gl/core';
import { GL } from '@luma.gl/webgl';
import proj4 from 'proj4';

import { TILE_CDN } from '../../config';

import type { renderBitmapTileSubLayer } from './renderBitmapTileSubLayer';

// The Malee QGIS Server keeps a pre-seeded tile cache in native Krovák
// (EPSG:5514). Requesting EPSG:3857 forces an on-the-fly reprojection per
// tile (measured ~4-5x slower, and cold tiles can time out), so these layers
// fetch the cached Krovák pyramid and reproject client-side instead.
const KROVAK_DEFINITION =
  '+proj=krovak +lat_0=49.5 +lon_0=24.8333333333333 ' +
  '+alpha=30.2881397527778 +k=0.9999 +x_0=0 +y_0=0 +ellps=bessel ' +
  // Rotations use the position-vector sign convention that proj4 +towgs84
  // expects. The published EPSG:5514 values are coordinate-frame (negated); the
  // opposite sign shifts every reprojected tile ~35m east. These signs match
  // PROJ's S-JTSK->WGS84 (1)/(5) to <0.1m and the WGS84 tiles and GPS markers.
  '+towgs84=572.213,85.334,461.94,4.9732,1.529,5.2484,3.5378 ' +
  '+units=m +no_defs +type=crs';

const MALEE_WMTS_URL =
  `${TILE_CDN}/malee/cgi-bin/qgis_mapserv.fcgi.exe` +
  '?MAP=D:/Basic/Basic.qgz&SERVICE=WMTS&REQUEST=GetTile&VERSION=1.0.0' +
  '&STYLE=default&FORMAT=image/jpeg&TILEMATRIXSET=EPSG:5514';

// EPSG:5514 TileMatrixSet from the server's WMTS GetCapabilities: 256px tiles,
// a quad-tree (matrixWidth = 2**z) anchored at a fixed top-left corner, with the
// scale denominator halving each level. The level-0 tile therefore spans
// LEVEL_0_SCALE_DENOMINATOR * 0.00028m/px * 256px in Krovák metres.
const TILE_SIZE = 256;
const OGC_METERS_PER_PIXEL = 0.00028;
const LEVEL_0_SCALE_DENOMINATOR = 21829890;
const MALEE_TOP_LEFT_X = -1347493.884;
const MALEE_TOP_LEFT_Y = -340108.696;
const MALEE_EXTENT_SIZE =
  LEVEL_0_SCALE_DENOMINATOR * OGC_METERS_PER_PIXEL * TILE_SIZE;

// Ground metres per pixel of the level-0 tile, used to derive the native level
// from the on-screen resolution.
const LEVEL_0_PIXEL_SPAN = LEVEL_0_SCALE_DENOMINATOR * OGC_METERS_PER_PIXEL;

// Ground metres per pixel of a web-mercator tile at zoom 0 and the equator.
const WEB_MERCATOR_ZOOM_0_RESOLUTION = (2 * Math.PI * 6378137) / TILE_SIZE;

// The server publishes levels 0-18; DMR5G stops gaining real detail well before
// that, so cap requests to keep tiles reasonably sized.
const MALEE_MAX_ZOOM = 16;

// Published coverage of the CUZK DMR5G (Czechia); avoids requesting tiles the
// server has no data for when the viewport extends past the border.
const MALEE_GEOGRAPHIC_EXTENT = {
  west: 12.0,
  south: 48.5,
  east: 18.9,
  north: 51.1,
};

// Warp grid per tile. Krovák is conformal and a tile spans ~1.5 km or less, so
// the projection is effectively linear across it; 8x8 tracks the curvature to
// well under a pixel while cutting per-tile proj4 work ~3.5x versus 16x16.
const REPROJECTION_MESH_SEGMENTS = 8;

const SLOPE_BLEND_PARAMETERS = {
  blendFunc: [
    GL.DST_COLOR,
    GL.ONE_MINUS_SRC_ALPHA,
    GL.ONE,
    GL.ONE_MINUS_SRC_ALPHA,
  ],
  blendEquation: [GL.FUNC_ADD, GL.FUNC_ADD],
} as BitmapLayerProps['parameters'];

interface TileIndex {
  x: number;
  y: number;
  z: number;
}

type JtskBounds = [left: number, bottom: number, right: number, top: number];

type GeographicCorners = [
  [number, number],
  [number, number],
  [number, number],
  [number, number]
];

interface TransformedMaleeTileMetadata {
  jtskBounds: JtskBounds;
  geographicCorners: GeographicCorners;
}

interface TransformedMaleeBitmapProps {
  jtskBounds: JtskBounds;
}

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.min(Math.max(value, minimum), maximum);
}

function wgs84ToJtsk(longitude: number, latitude: number): [number, number] {
  const [easting, northing] = proj4('EPSG:4326', KROVAK_DEFINITION, [
    longitude,
    latitude,
  ]);

  return [easting, northing];
}

function jtskToWgs84(easting: number, northing: number): [number, number] {
  const [longitude, latitude] = proj4(KROVAK_DEFINITION, 'EPSG:4326', [
    easting,
    northing,
  ]);

  return [longitude, latitude];
}

function getJtskTileBounds({ x, y, z }: TileIndex): JtskBounds {
  const span = MALEE_EXTENT_SIZE / 2 ** z;
  const left = MALEE_TOP_LEFT_X + x * span;
  const top = MALEE_TOP_LEFT_Y - y * span;

  return [left, top - span, left + span, top];
}

function getGeographicCorners(jtskBounds: JtskBounds): GeographicCorners {
  const [left, bottom, right, top] = jtskBounds;

  return [
    jtskToWgs84(left, bottom),
    jtskToWgs84(left, top),
    jtskToWgs84(right, top),
    jtskToWgs84(right, bottom),
  ];
}

function getGeographicBbox(corners: GeographicCorners) {
  const longitudes = corners.map(([longitude]) => longitude);
  const latitudes = corners.map(([, latitude]) => latitude);

  return {
    west: Math.min(...longitudes),
    south: Math.min(...latitudes),
    east: Math.max(...longitudes),
    north: Math.max(...latitudes),
  };
}

function getDevicePixelRatio(): number {
  return typeof window !== 'undefined' && window.devicePixelRatio
    ? window.devicePixelRatio
    : 1;
}

// Choose the native Krovák level closest to the screen's device-pixel density
// at the current latitude. Targeting device pixels (not CSS pixels) keeps tiles
// crisp on retina displays, and rounding to the nearest level (rather than
// always rounding up to the finer one) roughly halves the tiles per viewport:
// the finer level costs ~4x more on-demand renders on the origin for a
// difference that is barely visible, so the nearest level is the better trade.
function getNativeZoom(viewportZoom: number, latitude: number): number {
  const groundResolution =
    (WEB_MERCATOR_ZOOM_0_RESOLUTION * Math.cos((latitude * Math.PI) / 180)) /
    2 ** viewportZoom;
  const targetResolution = groundResolution / getDevicePixelRatio();
  const nativeZoom = Math.round(
    Math.log2(LEVEL_0_PIXEL_SPAN / targetResolution)
  );

  return clamp(nativeZoom, 0, MALEE_MAX_ZOOM);
}

export function getTransformedMaleeTileIndices(
  viewportBounds: [west: number, south: number, east: number, north: number],
  viewportZoom: number
): TileIndex[] {
  const [viewportWest, viewportSouth, viewportEast, viewportNorth] =
    viewportBounds;
  const west = Math.max(viewportWest, MALEE_GEOGRAPHIC_EXTENT.west);
  const south = Math.max(viewportSouth, MALEE_GEOGRAPHIC_EXTENT.south);
  const east = Math.min(viewportEast, MALEE_GEOGRAPHIC_EXTENT.east);
  const north = Math.min(viewportNorth, MALEE_GEOGRAPHIC_EXTENT.north);

  if (west >= east || south >= north) {
    return [];
  }

  const projectedCorners = [
    wgs84ToJtsk(west, south),
    wgs84ToJtsk(west, north),
    wgs84ToJtsk(east, north),
    wgs84ToJtsk(east, south),
  ];
  const eastings = projectedCorners.map(([easting]) => easting);
  const northings = projectedCorners.map(([, northing]) => northing);
  const minimumEasting = Math.min(...eastings);
  const maximumEasting = Math.max(...eastings);
  const minimumNorthing = Math.min(...northings);
  const maximumNorthing = Math.max(...northings);

  const z = getNativeZoom(viewportZoom, (south + north) / 2);
  const matrixSize = 2 ** z;
  const tileSpan = MALEE_EXTENT_SIZE / matrixSize;

  const firstX = clamp(
    Math.floor((minimumEasting - MALEE_TOP_LEFT_X) / tileSpan),
    0,
    matrixSize - 1
  );
  const lastX = clamp(
    Math.ceil((maximumEasting - MALEE_TOP_LEFT_X) / tileSpan) - 1,
    0,
    matrixSize - 1
  );
  const firstY = clamp(
    Math.floor((MALEE_TOP_LEFT_Y - maximumNorthing) / tileSpan),
    0,
    matrixSize - 1
  );
  const lastY = clamp(
    Math.ceil((MALEE_TOP_LEFT_Y - minimumNorthing) / tileSpan) - 1,
    0,
    matrixSize - 1
  );

  const indices: TileIndex[] = [];

  for (let x = firstX; x <= lastX; x += 1) {
    for (let y = firstY; y <= lastY; y += 1) {
      indices.push({ x, y, z });
    }
  }

  return indices;
}

class TransformedMaleeTileset extends Tileset2D {
  getTileIndices({ viewport }: { viewport: Viewport }): TileIndex[] {
    const bounds = viewport.getBounds() as [number, number, number, number];

    return getTransformedMaleeTileIndices(bounds, viewport.zoom);
  }

  getTileMetadata(index: TileIndex) {
    const jtskBounds = getJtskTileBounds(index);
    const geographicCorners = getGeographicCorners(jtskBounds);
    const userData: TransformedMaleeTileMetadata = {
      jtskBounds,
      geographicCorners,
    };

    return {
      bbox: getGeographicBbox(geographicCorners),
      userData,
    };
  }
}

function createReprojectedMesh(jtskBounds: JtskBounds) {
  const [left, bottom, right, top] = jtskBounds;
  const vertexSide = REPROJECTION_MESH_SEGMENTS + 1;
  const vertexCount = vertexSide * vertexSide;
  const positions = new Float64Array(vertexCount * 3);
  const texCoords = new Float32Array(vertexCount * 2);
  const indices = new Uint32Array(
    REPROJECTION_MESH_SEGMENTS * REPROJECTION_MESH_SEGMENTS * 6
  );

  let vertex = 0;
  let index = 0;

  for (let x = 0; x < vertexSide; x += 1) {
    const u = x / REPROJECTION_MESH_SEGMENTS;

    for (let y = 0; y < vertexSide; y += 1) {
      const v = y / REPROJECTION_MESH_SEGMENTS;
      const easting = left + (right - left) * u;
      const northing = bottom + (top - bottom) * v;
      const [longitude, latitude] = jtskToWgs84(easting, northing);

      positions[vertex * 3] = longitude;
      positions[vertex * 3 + 1] = latitude;
      positions[vertex * 3 + 2] = 0;
      texCoords[vertex * 2] = u;
      texCoords[vertex * 2 + 1] = 1 - v;

      if (x > 0 && y > 0) {
        indices[index] = vertex - vertexSide;
        indices[index + 1] = vertex - vertexSide - 1;
        indices[index + 2] = vertex - 1;
        indices[index + 3] = vertex - vertexSide;
        indices[index + 4] = vertex - 1;
        indices[index + 5] = vertex;
        index += 6;
      }

      vertex += 1;
    }
  }

  return {
    vertexCount: indices.length,
    positions,
    indices,
    texCoords,
  };
}

class TransformedMaleeBitmapLayer extends BitmapLayer<TransformedMaleeBitmapProps> {
  protected _createMesh() {
    return createReprojectedMesh(this.props.jtskBounds);
  }
}

type BitmapTileSubLayerProps = Parameters<typeof renderBitmapTileSubLayer>[0];

function renderTransformedMaleeSubLayer(props: BitmapTileSubLayerProps) {
  const { data, ...parentProps } = props;
  const metadata = props.tile.userData as TransformedMaleeTileMetadata;

  return [
    new TransformedMaleeBitmapLayer(parentProps, {
      image: data as NonNullable<BitmapLayerProps['image']>,
      bounds: metadata.geographicCorners as BitmapBoundingBox,
      jtskBounds: metadata.jtskBounds,
    }),
  ];
}

function renderTransformedMaleeSlopeSubLayer(props: BitmapTileSubLayerProps) {
  const { data, ...parentProps } = props;
  const metadata = props.tile.userData as TransformedMaleeTileMetadata;

  return [
    new TransformedMaleeBitmapLayer(parentProps, {
      image: data as NonNullable<BitmapLayerProps['image']>,
      bounds: metadata.geographicCorners as BitmapBoundingBox,
      jtskBounds: metadata.jtskBounds,
      parameters: SLOPE_BLEND_PARAMETERS,
    }),
  ];
}

function createTransformedMaleeLayer({
  id,
  wmtsLayer,
  renderSubLayers,
}: {
  id: string;
  wmtsLayer: string;
  renderSubLayers: (props: BitmapTileSubLayerProps) => BitmapLayer[];
}) {
  return new TileLayer({
    id,
    TilesetClass: TransformedMaleeTileset,
    getTileData: async ({ index: { x, y, z }, signal }) => {
      const url =
        `${MALEE_WMTS_URL}&LAYER=${wmtsLayer}` +
        `&TILEMATRIX=${z}&TILEROW=${y}&TILECOL=${x}`;
      const response = await fetch(url, { signal });

      if (!response.ok) {
        throw new Error(
          `Malee Krovák tile request failed with ${response.status}`
        );
      }

      return parse(response);
    },
    // The origin renderer collapses under concurrency: browser tests show a
    // cold viewport (150 tiles) takes ~18s at 150 parallel requests but ~5.6s
    // when capped, and it degrades again past ~10 combined. Both Krovák layers
    // (SLRM + Slope) run at once with independent request pools, so cap each at
    // 5 to keep the combined load near the ~10 sweet spot. Edge-cached (warm)
    // tiles are unaffected — they return in tens of milliseconds regardless.
    maxRequests: 5,
    pickable: true,
    highlightColor: [60, 60, 60, 40],
    minZoom: 0,
    maxZoom: MALEE_MAX_ZOOM,
    tileSize: TILE_SIZE,
    opacity: 1,
    renderSubLayers,
  });
}

export const transformedDmrMaleeSlrm = () =>
  createTransformedMaleeLayer({
    id: 'transformed-malee-dmr-slrm-layer',
    wmtsLayer: 'CUZK.DMR5G.RVT.SLRM.Z10',
    renderSubLayers: renderTransformedMaleeSubLayer,
  });

export const transformedDmrMaleeSlope = () =>
  createTransformedMaleeLayer({
    id: 'transformed-malee-dmr-slope-layer',
    wmtsLayer: 'CUZK.DMR5G.RVT.Slope.Z5',
    renderSubLayers: renderTransformedMaleeSlopeSubLayer,
  });
